import React, { useState, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';

import LabeledContainer from '../../../../common/LabeledContainer';
import BooksSelectedDateTimeWithChange from '../BooksSelectedDateTimeWithChange';
import TextInput, { TextInputType } from '../../../../../atoms/TextInput';
import BooksWaitingListAlert from '../BooksWaitingListAlert';

import ErrorMessage from '../InputBookingWaitingListErrorMessage';

import {
  TimeSlot,
  WaitingListErrors,
  EventTimeSlot,
} from '../../../../../interfaces/merchants/resources/books';
import { SubmitButton } from '../../../../../atoms/SubmitButton';

interface Props {
  postable: boolean;
  timeSlot: TimeSlot;
  errors?: WaitingListErrors;
  onAddToWaitingList: (
    timeSlot: EventTimeSlot | undefined,
    lastName?: string,
    firstName?: string,
    email?: string,
    onReturnSelectDateTime?: () => void,
  ) => Promise<void>;
  onReturnSelectDateTime: () => void;
}

const InputBookingWaitingList: React.FC<Props> = (props) => {
  const [lastName, setLastName] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const submittable = useMemo(() => {
    return lastName && firstName && email;
  }, [lastName, firstName, email]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      currentTarget: { name, value },
    } = e;

    switch (name) {
      case 'lastName':
        setLastName(value);
        break;
      case 'firstName':
        setFirstName(value);
        break;
      case 'email':
        setEmail(value);
        break;
      default:
        break;
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    const { timeSlot, onAddToWaitingList, onReturnSelectDateTime } = props;

    await onAddToWaitingList(
      timeSlot,
      lastName,
      firstName,
      email,
      onReturnSelectDateTime,
    );
  }, [lastName, firstName, email, props]);

  const { timeSlot, errors, onReturnSelectDateTime, postable } = props;
  const intl = useIntl();

  return (
    <div>
      <BooksWaitingListAlert />
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.selectedDateTime',
        })}
        paddingTop={true}
        contentsPadding={true}
      >
        <BooksSelectedDateTimeWithChange
          date={timeSlot.date}
          startTime={timeSlot.startTime}
          endTime={timeSlot.endTime}
          changeTarget={onReturnSelectDateTime}
        />
      </LabeledContainer>
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.lastName',
        })}
        paddingTop={true}
      >
        <TextInput
          type={TextInputType.Text}
          name={'lastName'}
          value={lastName}
          onChange={handleChange}
        />
      </LabeledContainer>
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.firstName',
        })}
      >
        <TextInput
          type={TextInputType.Text}
          name={'firstName'}
          value={firstName}
          onChange={handleChange}
        />
      </LabeledContainer>
      <LabeledContainer
        label={intl.formatMessage({
          id: 'books.formsLabel.email',
        })}
      >
        <TextInput
          type={TextInputType.Email}
          name={'email'}
          value={email}
          onChange={handleChange}
        />
      </LabeledContainer>
      <ErrorMessage errors={errors && errors.email} />
      <SubmitButton
        type="button"
        onClick={handleSubmit}
        value={intl.formatMessage({ id: 'books.submitLabel.addWaitingList' })}
        disabled={!submittable || !postable}
        postable={postable}
      >
        {intl.formatMessage({ id: 'books.submitLabel.addWaitingList' })}
      </SubmitButton>
    </div>
  );
};

export default InputBookingWaitingList;
